import React, { useEffect, useRef, useState } from 'react';

const Turnstile = ({ siteKey, onVerify, onError, action = 'vote_submission' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Detect if we're running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);
  
  // Track if we've already initialized Turnstile to prevent multiple renders
  const isInitialized = useRef(false);
  
  // Load Turnstile script only once on mount
  useEffect(() => {
    console.log(`Initializing Turnstile with site key: ${siteKey}`);
    console.log(`Running on ${isLocalhost ? 'localhost' : 'production'} environment`);
    
    // Prevent multiple initializations
    if (isInitialized.current) {
      console.log('Turnstile already initialized, skipping');
      return;
    }
    
    isInitialized.current = true;
    
    // Load the Turnstile script if it's not already loaded
    if (!window.turnstile) {
      setIsLoading(true);
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (!isMounted.current) return;
        console.log('Turnstile script loaded successfully');
        setScriptLoaded(true);
        setIsLoading(false);
        // Wait for next render cycle to ensure containerRef is available
        setTimeout(() => {
          if (isMounted.current) renderTurnstile();
        }, 0);
      };

      script.onerror = (error) => {
        if (!isMounted.current) return;
        console.error('Failed to load Turnstile script:', error);
        setIsLoading(false);
        if (onError) onError('Failed to load verification script');
      };

      return () => {
        // Only remove if we added it
        try {
          document.body.removeChild(script);
        } catch (e) {
          // Script might have been removed already
        }
      };
    } else {
      console.log('Turnstile script already loaded');
      setScriptLoaded(true);
      setIsLoading(false);
      // Wait for next render cycle
      setTimeout(() => {
        if (isMounted.current) renderTurnstile();
      }, 0);
    }
    
    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, []);  // Empty dependency array - only run once on mount

  const renderTurnstile = () => {
    // Prevent rendering if component is unmounted or container not ready
    if (!isMounted.current || !containerRef.current) {
      console.log('Cannot render Turnstile: component unmounted or container not ready');
      return;
    }
    
    // Check if Turnstile script is loaded
    if (!window.turnstile) {
      console.log('Cannot render Turnstile: script not loaded');
      return;
    }

    try {
      // Only render if we don't already have a widget ID
      if (widgetIdRef.current) {
        console.log('Turnstile widget already exists with ID:', widgetIdRef.current);
        return;
      }

      console.log('Rendering Turnstile widget with options:', {
        sitekey: siteKey,
        action: action
      });

      // For localhost/Docker, use a mock implementation
      if (isLocalhost) {
        console.log('Using mock implementation for localhost/Docker');
        // Create a fake token after a short delay to simulate verification
        setTimeout(() => {
          if (!isMounted.current) return;
          const mockToken = 'DEV_' + Math.random().toString(36).substring(2, 15);
          console.log('Generated mock token:', mockToken);
          if (onVerify) onVerify(mockToken);
        }, 500);
        return;
      }

      // Render the Turnstile widget with minimal options
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => {
          console.log('Turnstile verification successful');
          if (onVerify) onVerify(token);
        },
        'error-callback': (error) => {
          console.error('Turnstile verification error:', error);
          if (onError) onError(`Verification failed: ${error || 'Unknown error'}`);
        },
        action: action
      });
      
      console.log('Turnstile widget rendered with ID:', widgetIdRef.current);
    } catch (error) {
      console.error('Error rendering Turnstile widget:', error);
      if (onError) onError(`Failed to render verification: ${error.message}`);
    }
  };

  // Clean up the widget when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.error('Error removing Turnstile widget:', e);
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  // For localhost, render a mock implementation
  if (isLocalhost) {
    return (
      <div className="my-4">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-center">
          <p className="font-medium">Development Environment</p>
          <p className="text-gray-600 mt-1 mb-2">Auto-verifying for local development...</p>
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }
  
  // For production, render the actual Turnstile widget
  return <div ref={containerRef} className="my-4"></div>;
};

export default Turnstile;
